import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandHoldingMedical } from '@fortawesome/free-solid-svg-icons';
import { Helmet } from 'react-helmet-async';
import ChatbotAi from "../components/ChatbotAi";
import '../css/calculatorpages.css';

const HLVCalculator = () => {
    const [annualIncome, setAnnualIncome] = useState('');
    const [workingYearsRemaining, setWorkingYearsRemaining] = useState('');
    const [inflationAdjustment, setInflationAdjustment] = useState('');
    const [existingSavings, setExistingSavings] = useState('');

    const [estimatedHlv, setEstimatedHlv] = useState(null);
    const [totalEarningsPotential, setTotalEarningsPotential] = useState(null);
    const [existingSavingsDeducted, setExistingSavingsDeducted] = useState(null);

    const navigate = useNavigate();

    const handleBackClick = () => {
        navigate('/#financial-calculators-section');
    }

    const calculateHLV = () => {
        const income = parseFloat(annualIncome);
        const years = parseFloat(workingYearsRemaining);
        const inflationRate = parseFloat(inflationAdjustment) / 100;
        const savings = parseFloat(existingSavings) || 0;

        if (income > 0 && years > 0 && inflationRate >= 0) {
            let futureIncomeStream = 0;
            for (let i = 0; i < years; i++) {
                futureIncomeStream += income * Math.pow(1 + inflationRate, i);
            }

            const calculatedHlv = futureIncomeStream - savings;

            setTotalEarningsPotential(futureIncomeStream.toFixed(2));
            setExistingSavingsDeducted(savings.toFixed(2));
            setEstimatedHlv(calculatedHlv.toFixed(2));
        } else {
            setEstimatedHlv(null);
            setTotalEarningsPotential(null);
            setExistingSavingsDeducted(null);
        }
    };

    return (
        <div>
            <Helmet>
                <title>Human Life Value Calculator | Assess Your Insurance Needs</title>
                <meta name="description" content="Estimate your Human Life Value effectively to determine suitable life insurance coverage. Use our expert calculator for personalized advice." />
                <link rel="canonical" href="https://policysamjho.com/calculators/hlv-calculator/" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "SoftwareApplication",
                        "name": "Human Life Value Calculator",
                        "description": "Estimate your Human Life Value effectively to determine suitable life insurance coverage. Use our expert calculator for personalized advice.",
                        "url": "https://policysamjho.com/calculators/hlv-calculator/",
                        "applicationCategory": "FinancialApplication",
                        "operatingSystem": "Web",
                        "offers": {
                            "@type": "Offer",
                            "price": "0",
                            "priceCurrency": "INR"
                        },
                        "provider": {
                            "@type": "Organization",
                            "name": "Policy Samjho",
                            "url": "https://policysamjho.com/"
                        }
                    })}
                </script>
            </Helmet>

            <div className="calculator-page-container">
                <button onClick={handleBackClick} className='calculator-back-button'>
                    Back
                </button>
                <div className="calculator-content-wrapper">
                    <div className="calculator-info-section">
                        <div className="calculator-header-content">
                            <FontAwesomeIcon icon={faHandHoldingMedical} className="calculator-header-icon" />
                            <h1 className="calculator-title">Human Life Value Calculator</h1>
                        </div>
                        <p className="calculator-description">Human Life Value (HLV) estimates the present value of future income, expenses, liabilities and investments, helping determine the required insurance coverage or financial planning.</p>
                    </div>

                    <div className={`calculator-main-section ${estimatedHlv === null ? 'no-result' : ''}`}>
                        <div className="calculator-input-section">
                            <div className="input-row-group">
                                <div className="input-group">
                                    <label htmlFor="annualIncome">Annual Income (₹)</label>
                                    <input
                                        type="number"
                                        id="annualIncome"
                                        value={annualIncome}
                                        onChange={(e) => setAnnualIncome(e.target.value)}
                                        placeholder="e.g. 800000"
                                    />
                                </div>
                                <div className="input-group">
                                    <label htmlFor="workingYearsRemaining">Expected Working Years Remaining</label>
                                    <input
                                        type="number"
                                        id="workingYearsRemaining"
                                        value={workingYearsRemaining}
                                        onChange={(e) => setWorkingYearsRemaining(e.target.value)}
                                        placeholder="e.g. 25"
                                    />
                                </div>
                            </div>
                            <div className="input-row-group">
                                <div className="input-group">
                                    <label htmlFor="inflationAdjustment">Inflation Adjustment (%)</label>
                                    <input
                                        type="number"
                                        id="inflationAdjustment"
                                        value={inflationAdjustment}
                                        onChange={(e) => setInflationAdjustment(e.target.value)}
                                        placeholder="e.g. 5"
                                    />
                                </div>
                                <div className="input-group">
                                    <label htmlFor="existingSavings">Existing Savings / Assets (₹)</label>
                                    <input
                                        type="number"
                                        id="existingSavings"
                                        value={existingSavings}
                                        onChange={(e) => setExistingSavings(e.target.value)}
                                        placeholder="e.g. 1000000"
                                    />
                                </div>
                            </div>
                            <button onClick={calculateHLV} className="calculate-button">Calculate</button>
                        </div>

                        {estimatedHlv !== null && (
                            <div className="calculator-result-section">
                                <h2>Result</h2>
                                <div className="result-item">
                                    <span>Estimated HLV</span>
                                    <span className="result-value">₹{estimatedHlv}</span>
                                </div>
                                <div className="result-item">
                                    <span>Total Earnings Potential</span>
                                    <span className="result-value">₹{totalEarningsPotential}</span>
                                </div>
                                <div className="result-item">
                                    <span>Existing Savings Deducted</span>
                                    <span className="result-value">₹{existingSavingsDeducted}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <ChatbotAi />
        </div>
    );
};

export default HLVCalculator;